import { useState } from 'react';
import { Handshake, ShieldCheck, Stethoscope, Truck, User } from 'lucide-react';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import { photoUrl, slugify, useSite } from '../../utils/site';

// Source of truth: For Webpage_SKG.pptx ("Partners Information" slide).
//
// Photos: uploaded on Administration -> Appearance -> Photos, else the
// built-in public/partners/<slug>.jpg; with neither, the initials show.

const DESIGNATED_PARTNERS = ['Babu MG', 'Nagesh Babu BV', 'Vinay Kumar T'];
const PARTNERS = ['Ramesh MD', 'Muralidhara MR', 'Nanjunda Reddy K', 'Ramesh Reddy'];

const PARTNER_TYPES = [
  {
    icon: Handshake,
    title: 'Dairy Co-operatives',
    description: 'We work with local dairy co-operatives to ensure fair pricing and reliable collection.',
  },
  {
    icon: Stethoscope,
    title: 'Veterinary Partners',
    description: 'Regular veterinary support keeps our herd and flock healthy year-round.',
  },
  {
    icon: Truck,
    title: 'Distribution Partners',
    description: 'Trusted logistics partners help us get fresh products to customers on time.',
  },
  {
    icon: ShieldCheck,
    title: 'Certifying Bodies',
    description: 'We work towards recognised quality and safety standards through relevant certifying bodies.',
  },
];

function PartnerCard({ name, title }: { name: string; title: string }) {
  const site = useSite();
  const [photoFailed, setPhotoFailed] = useState(false);
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="flex flex-col items-center rounded-xl border border-brand-green-100 bg-surface p-6 text-center shadow-card transition-shadow hover:shadow-popover">
      {photoFailed ? (
        <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-green-600 to-brand-green-800 text-xl font-bold text-white shadow-card">
          {initials}
        </div>
      ) : (
        <img
          src={photoUrl(site, `partner.${slugify(name)}`) ?? ''}
          alt={name}
          onError={() => setPhotoFailed(true)}
          className="mb-3 h-24 w-24 rounded-full border-2 border-brand-gold-200 object-cover object-top shadow-card"
        />
      )}
      <h3 className="text-base font-semibold text-brand-green-900">{name}</h3>
      <p className="mt-1 text-sm text-ink-500">{title}</p>
    </div>
  );
}

export default function Partners() {
  return (
    <section id="partners" className="scroll-mt-20">
      <section className="bg-gradient-to-b from-brand-green-50 to-surface py-16 sm:py-6">
        <Container>
          <SectionHeading
            eyebrow="Our Partners"
            title="The people behind Sri KalpaGavya"
            subtitle="Sri KalpaGavya Dairy and Farms LLP is guided by a team of designated partners and partners committed to ethical, sustainable farming."
            center
          />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="space-y-12">
          <div>
            <h2 className="mb-6 flex items-center gap-2 text-xl font-bold text-brand-green-900">
              <User className="h-5 w-5 text-brand-gold-600" /> Designated Partners
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {DESIGNATED_PARTNERS.map((name) => (
                <PartnerCard key={name} name={name} title="Designated Partner" />
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-6 flex items-center gap-2 text-xl font-bold text-brand-green-900">
              <User className="h-5 w-5 text-brand-gold-600" /> Partners
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {PARTNERS.map((name) => (
                <PartnerCard key={name} name={name} title="Partner" />
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-surface-subtle py-16 sm:py-20">
        <Container>
          <SectionHeading
            eyebrow="Our Partnership Philosophy"
            title="Why partnerships matter to us"
            subtitle="We believe good farming doesn't happen in isolation. By partnering with co-operatives, veterinary experts, distribution networks and certifying bodies, we can focus on what we do best — raising healthy animals and growing quality produce — while our partners help us reach further, farm more responsibly, and stand behind the quality of what we deliver."
          />

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PARTNER_TYPES.map((p) => (
              <div key={p.title} className="rounded-xl border border-brand-green-100 bg-surface p-6 text-center shadow-card">
                <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green-50">
                  <p.icon className="h-6 w-6 text-brand-green-700" />
                </div>
                <h3 className="text-base font-semibold text-brand-green-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-500">{p.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </section>
  );
}
